import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, AlertCircle } from 'lucide-react';
import SEO from '../components/SEO';
import './StaticPages.css';

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="static-page">
      <SEO 
        title="Privacy Policy - RazChatz | Your Data Security Matters"
        description="Read RazChatz's comprehensive privacy policy. Learn how we collect, use, protect your data with end-to-end encryption and GDPR compliance."
        keywords="privacy policy, data protection, GDPR, end-to-end encryption, secure chat, data security, user privacy, razchatz privacy"
        canonical="https://razchatz.netlify.app/privacy"
        ogTitle="RazChatz Privacy Policy - We Protect Your Data"
        ogDescription="Comprehensive privacy policy explaining how RazChatz protects your personal information with industry-leading security measures."
      />
      <div className="static-page-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
          Back to Home
        </button>
      </div>

      <div className="static-page-container">
        <h1 className="static-page-title">Privacy Policy</h1>
        <p className="last-updated">Last Updated: December 13, 2025</p>
        
        <div className="static-page-content">
          <section className="policy-intro">
            <p className="lead-text">
              At RazChatz, we take your privacy seriously. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you use our chat platform. Please read 
              this privacy policy carefully.
            </p>
          </section>

          <section className="policy-section">
            <div className="policy-icon-wrapper">
              <Database className="policy-icon" />
            </div>
            <h2>Information We Collect</h2>
            
            <h3>Personal Information</h3>
            <p>When you register for RazChatz, we collect:</p>
            <ul>
              <li>Username</li>
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Profile information you choose to provide</li>
            </ul>

            <h3>Usage Information</h3>
            <p>We automatically collect certain information when you use our platform:</p>
            <ul>
              <li>Device information (type, operating system, browser)</li>
              <li>IP address and location data</li>
              <li>Usage patterns and preferences</li>
              <li>Chat metadata (timestamps, participants)</li>
            </ul>

            <h3>Guest Users</h3>
            <p>
              For guest chatting, we collect minimal information necessary to provide the service, 
              including temporary session data and basic device information.
            </p>
          </section>

          <section className="policy-section">
            <div className="policy-icon-wrapper">
              <Eye className="policy-icon" />
            </div>
            <h2>How We Use Your Information</h2>
            <p>We use the collected information for:</p>
            <ul>
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve RazChatz services</li>
              <li><strong>Communication:</strong> To send you updates, notifications, and support messages</li>
              <li><strong>Security:</strong> To detect, prevent, and address fraud and security issues</li>
              <li><strong>Personalization:</strong> To customize your experience based on preferences</li>
              <li><strong>Analytics:</strong> To understand usage patterns and improve our platform</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our terms</li>
            </ul>
          </section>

          <section className="policy-section">
            <div className="policy-icon-wrapper">
              <Lock className="policy-icon" />
            </div>
            <h2>Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information:
            </p>
            <ul>
              <li><strong>Encryption:</strong> End-to-end encryption for all messages</li>
              <li><strong>Secure Storage:</strong> Encrypted database storage for user data</li>
              <li><strong>Access Controls:</strong> Strict access limitations to personal information</li>
              <li><strong>Regular Audits:</strong> Periodic security assessments and updates</li>
              <li><strong>HTTPS:</strong> Secure connections for all data transmission</li>
            </ul>
            <p>
              While we strive to protect your information, no method of transmission over the internet 
              is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section className="policy-section">
            <div className="policy-icon-wrapper">
              <UserCheck className="policy-icon" />
            </div>
            <h2>Information Sharing</h2>
            <p>We do not sell your personal information. We may share information in these situations:</p>
            <ul>
              <li><strong>With Your Consent:</strong> When you explicitly agree to share information</li>
              <li><strong>Service Providers:</strong> With trusted third parties who assist in operating our platform</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect rights and safety</li>
              <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
            </ul>
            <p>
              All third-party service providers are contractually obligated to maintain the confidentiality 
              and security of your information.
            </p>
          </section>

          <section className="policy-section">
            <div className="policy-icon-wrapper">
              <Shield className="policy-icon" />
            </div>
            <h2>Your Rights and Choices</h2>
            <p>You have the following rights regarding your information:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Data Portability:</strong> Request your data in a portable format</li>
              <li><strong>Restriction:</strong> Limit how we process your information</li>
            </ul>
            <p>
              To exercise these rights, please contact us at <strong>support@razchatz.com</strong>
            </p>
          </section>

          <section className="policy-section">
            <h2>Cookies and Tracking</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your experience:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for platform functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand usage patterns</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            </ul>
            <p>
              You can control cookies through your browser settings. Note that disabling cookies may 
              affect platform functionality.
            </p>
          </section>

          <section className="policy-section">
            <h2>Children's Privacy</h2>
            <p>
              RazChatz is not intended for users under 13 years of age. We do not knowingly collect 
              information from children under 13. If we discover that we have collected information 
              from a child under 13, we will promptly delete it.
            </p>
          </section>

          <section className="policy-section">
            <h2>International Users</h2>
            <p>
              RazChatz is operated globally. By using our service, you consent to the transfer of 
              your information to countries outside your residence, which may have different data 
              protection laws.
            </p>
          </section>

          <section className="policy-section">
            <h2>Data Retention</h2>
            <p>
              We retain your information for as long as necessary to provide services and comply with 
              legal obligations. When you delete your account, we remove your personal information 
              within 30 days, except where retention is required by law.
            </p>
          </section>

          <section className="policy-section">
            <div className="policy-icon-wrapper">
              <AlertCircle className="policy-icon" />
            </div>
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy periodically. We will notify you of significant changes 
              via email or platform notification. Your continued use of RazChatz after changes indicates 
              acceptance of the updated policy.
            </p>
          </section>

          <section className="policy-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy, please contact us:
            </p>
            <ul className="contact-info">
              <li><strong>Email:</strong> privacy@razchatz.com</li>
              <li><strong>Support:</strong> support@razchatz.com</li>
              <li><strong>Website:</strong> https://razchatz.netlify.app</li>
            </ul>
          </section>

          <section className="cta-section">
            <p>
              By using RazChatz, you acknowledge that you have read and understood this Privacy Policy.
            </p>
            <div className="cta-buttons">
              <button className="cta-button primary" onClick={() => navigate('/signup')}>
                Get Started
              </button>
              <button className="cta-button secondary" onClick={() => navigate('/terms')}>
                Read Terms of Service
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
